import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {CloseOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Calendar, Segmented, Space, Table, Tag, theme} from 'antd';
import {SorterResult} from 'antd/es/table/interface';
import classNames from 'classnames';
import {Shell} from 'components/Shell';
import {observer} from 'mobx-react-lite';
import {AppRoutePaths} from 'routing/Routes';
import {appState} from 'store/AppStore';
import {ServicesState} from 'store/services/ServicesState';
import {ServiceActionListDto} from '../../../../../../PRMGApiClient';
import {CalendarCell, paginationProps, ServiceSegment, serviceSegments} from '../utils/ServicesUtils';
import {serviceActionsColumns} from '../utils/ServiceTableColumns';

export const ServiceListView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [state] = useState(() => new ServicesState());
  const [searchParams, setSearchParams] = useSearchParams();
  const [segment, setSegment] = useState<ServiceSegment>(ServiceSegment.Table);

  const {token} = theme.useToken();

  const data = useMemo(() => state.items, [state.items]);

  useEffect(() => {
    if (segment === ServiceSegment.Table) state.loadTableItems();
    else if (segment === ServiceSegment.Calendar) state.loadCalendarItems();
  }, [state, segment]);

  useEffect(() => {
    const vehicleId = searchParams.get('vehicleId');
    if (vehicleId) state.setFiltersVehicle(Number(vehicleId));
  }, [searchParams, setSearchParams, state]);

  return (
    <Shell
      title={t('Services')}
      actionButtons={[
        <Button
          type="primary"
          key="add"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => navigate(AppRoutePaths.serviceCreate.url)}
          className="shell-btn"
        >
          {t('Add Service')}
        </Button>,
      ]}
    >
      <Space direction="vertical" className="w-full">
        <Space className={classNames('flex items-end', state.filtersCarName ? 'justify-between' : 'justify-end')}>
          {state.filtersCarName && (
            <Tag
              color="volcano"
              closable
              closeIcon={<CloseOutlined style={{color: token['volcano-7']}} />}
              className="h-max"
              onClose={() => {
                searchParams.delete('vehicleId');
                setSearchParams(searchParams);
                state.setFiltersVehicle();
              }}
            >
              {state.filtersCarName}
            </Tag>
          )}
          <Segmented options={serviceSegments} onChange={(value) => setSegment(value as ServiceSegment)} />
        </Space>
        {segment === ServiceSegment.Table ? (
          <div className="overflow-scroll">
            <Table
              rowKey="id"
              columns={serviceActionsColumns}
              dataSource={data}
              loading={appState.isLoading}
              pagination={{
                ...paginationProps,
                current: state.pageNumber,
                total: state.total,
                pageSize: state.pageSize,
              }}
              onChange={(pagination, filters, sorters) => {
                state.setPagination(pagination);
                state.setFilters(filters);
                state.setSorters(sorters as SorterResult<ServiceActionListDto>);
              }}
            />
          </div>
        ) : (
          <Calendar
            dateCellRender={(value) => <CalendarCell date={value.toDate()} items={state.items} type="day" />}
            monthCellRender={(value) => <CalendarCell date={value.toDate()} items={state.items} type="month" />}
            onPanelChange={(value, mode) => state.setFiltersCalendar(value.toDate(), mode)}
          />
        )}
      </Space>
    </Shell>
  );
});
